import { useEffect } from "preact/hooks";
import { track } from "../host/telemetry.js";
import type { Hosting } from "../net/api.js";
import { navigate } from "../router.js";
import { nextFreeLabel, passPage } from "./pass.js";

/** Offers seen once per page and reason (paywall_viewed, docs/metrics.md). */
const seen = new Set<string>();
function viewedOnce(reason: "hosting_limit" | "pass_missions", roomId: string | null) {
  if (seen.has(`${reason}:${roomId ?? ""}`)) return;
  seen.add(`${reason}:${roomId ?? ""}`);
  track("paywall_viewed", { reason, room_id: roomId });
}

/** Reads the api's hosting_limit reply (a room token or GET /api/me), if it is one. */
export function hostingOf(details: Record<string, unknown> | undefined): Hosting | null {
  const limit = details?.["limit"];
  const nextFreeAt = details?.["nextFreeAt"];
  const signInFor = details?.["signInFor"];
  return typeof limit === "number" && typeof nextFreeAt === "number"
    ? {
        limit,
        used: typeof details?.["used"] === "number" ? details["used"] : limit,
        nextFreeAt,
        ...(typeof signInFor === "number" ? { signInFor } : {}),
      }
    : null;
}

/**
 * The host has opened their free rooms for today (ADR 0022). Nothing they're already playing
 * stops; this is only a new room. Joining and solo stay free, and the Pass makes hosting unlimited.
 * A guest who could sign in for more free rooms is offered that first (ADR 0030).
 */
export function HostingLimit(props: { hosting: Hosting; roomId: string | null; back: string }) {
  useEffect(() => {
    viewedOnce("hosting_limit", props.roomId);
  }, [props.roomId]);
  const { limit, nextFreeAt, signInFor } = props.hosting;
  return (
    <div class="card pass">
      <h2>That's today's free rooms</h2>
      <p>
        Hosting is free for {limit} {limit === 1 ? "room" : "rooms"} a day, and you've opened{" "}
        {limit === 1 ? "it" : "them"}. Your next free room is{" "}
        {nextFreeAt === null ? "soon" : nextFreeLabel(nextFreeAt, Date.now())}.
      </p>
      <p>Joining a friend's room is always free, and so is playing solo.</p>
      {signInFor !== undefined && (
        <a
          class="button"
          href={`/signin?next=${encodeURIComponent(props.back)}`}
          onClick={() => {
            track("sign_in_from_limit", { room_id: props.roomId, rooms: signInFor });
          }}
        >
          Sign in for {signInFor} free rooms a day
        </a>
      )}
      <a
        class={signInFor === undefined ? "button" : "button secondary"}
        href={passPage(props.back)}
      >
        Host as much as you like with Obean Pass
      </a>
      <p class="hint">
        <a href="/solo">Play solo</a> ·{" "}
        <a
          href="/new"
          onClick={(e) => {
            e.preventDefault();
            navigate("/new");
          }}
        >
          Back to the start
        </a>
      </p>
    </div>
  );
}

/**
 * Missions only the Pass unlocks (ADR 0022), between matches. Anyone in the room with the Pass
 * unlocks them for everyone, so everyone sees this, not only the host.
 */
export function PassMissions(props: { missions: readonly string[]; roomId: string }) {
  useEffect(() => {
    viewedOnce("pass_missions", props.roomId);
  }, [props.roomId]);
  return (
    <div class="card pass">
      <h2>Pass missions</h2>
      <p>
        {props.missions.join(", ")}: anyone in this room with Obean Pass unlocks{" "}
        {props.missions.length === 1 ? "it" : "them"} for everyone.
      </p>
      <a class="button secondary" href={passPage(`/r/${props.roomId}`)}>
        About Obean Pass
      </a>
    </div>
  );
}
